import { useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { IconLoader2 } from "@tabler/icons-react";
import { toast } from "sonner";
import { loginWithProvider } from "../api";
import { useAuthStore } from "../stores/authStore";
import { nextRouteFor } from "../lib/postLogin";
import { consumePendingOAuth, redirectUriFor, type Provider } from "../lib/oauth";

const PROVIDERS: Provider[] = ["google", "microsoft", "apple"];

function isProvider(value: string | undefined): value is Provider {
  return value !== undefined && (PROVIDERS as string[]).includes(value);
}

/**
 * Recebe o retorno do provider em /auth/callback/:provider (?code&state),
 * valida o state (anti-CSRF), troca o code pelo agency-api e abre a sessão.
 */
export function OAuthCallbackPage() {
  const { provider } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);

  // StrictMode monta o efeito duas vezes em dev — garante uma única troca de code.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    function fail() {
      toast.error("Não foi possível concluir o login social. Tente novamente.");
      navigate("/login", { replace: true });
    }

    async function run() {
      if (!isProvider(provider)) {
        fail();
        return;
      }

      const providerError = searchParams.get("error");
      const code = searchParams.get("code");
      const state = searchParams.get("state");

      if (providerError || !code) {
        fail();
        return;
      }

      try {
        const pending = consumePendingOAuth(provider, state);
        const session = await loginWithProvider(provider, {
          code,
          redirectUri: redirectUriFor(provider),
          codeVerifier: pending.codeVerifier,
        });
        setSession(session);
        navigate(nextRouteFor(session.user), { replace: true });
      } catch {
        // Inclui OAuthStateError (state ausente/divergente) e falha na troca do code.
        fail();
      }
    }

    void run();
  }, [provider, searchParams, navigate, setSession]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-3 text-ink-muted">
      <IconLoader2 className="animate-spin" size={28} aria-hidden />
      <p className="text-sm">Concluindo seu acesso…</p>
    </div>
  );
}
